import Layout from '@/layouts/Layout';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileText, Plus } from 'lucide-react';
import { router, usePage, useRemember } from '@inertiajs/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logError } from '@/lib/logger';
import { csrfFetch } from '@/lib/csrf';
import { useDebounce } from '@/hooks/useDebounce';

import ClinicalNoteEditor from '@/components/patient/clinical-notes/ClinicalNoteEditor';
import ClinicalNoteViewer from '@/components/patient/clinical-notes/ClinicalNoteViewer';
import ClinicalNotesSidebar from '@/components/patient/clinical-notes/ClinicalNotesSidebar';
import type {
  ClinicalNote,
  ClinicalNoteSummary,
  EditorMode,
  NormalizedClinicalNote,
} from '@/components/patient/clinical-notes/types';
import { PatientPageHeader } from '@/components/patient';
import { breadcrumbs } from '@/components/ui/breadcrumb';

interface PatientSummary {
  id: number;
  display_name: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  demo?: boolean;
}

export interface Props {
  patient: PatientSummary;
  notes: ClinicalNote[];
  selectedNoteId?: number | null;
  clinicalNotesMeta: {
    count: number;
    last_updated_at: string | null;
  };
  hideChrome?: boolean;
}

interface StoredClinicalNoteDraft {
  body: string;
  baselineBody: string;
  savedAt: string;
}

type DraftContext = { mode: 'create' } | { mode: 'edit'; noteId: number };

interface RecoveryDialogState {
  context: DraftContext;
  draft: StoredClinicalNoteDraft;
  storageKey: string;
  hasConflict: boolean;
}

const CLINICAL_NOTE_DRAFT_STORAGE_VERSION = 'v1';
const CLINICAL_NOTE_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const CLINICAL_NOTE_DRAFT_STORAGE_PREFIX = [
  'emovia',
  'clinical-notes',
  'drafts',
  CLINICAL_NOTE_DRAFT_STORAGE_VERSION,
].join(':');

const EMPTY_SUMMARY: ClinicalNoteSummary = {
  topics: [],
  concerns: [],
  goals: [],
  progress: [],
  challenges: [],
  successes: [],
  description: '',
};

function normalizeSummary(summary?: Partial<ClinicalNoteSummary> | null): ClinicalNoteSummary {
  if (!summary) return EMPTY_SUMMARY;
  return {
    topics: summary.topics ?? [],
    concerns: summary.concerns ?? [],
    goals: summary.goals ?? [],
    progress: summary.progress ?? [],
    challenges: summary.challenges ?? [],
    successes: summary.successes ?? [],
    description: summary.description ?? '',
  };
}

function normalizeNote(note: ClinicalNote): NormalizedClinicalNote {
  return {
    ...note,
    summary: normalizeSummary(note.summary),
  };
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
}

function createPreview(body: string) {
  const trimmed = body.trim();
  if (!trimmed) return 'Nota vuota';
  const firstLine = trimmed.split(/\n/)[0];
  return firstLine.length > 120 ? `${firstLine.slice(0, 120)}…` : firstLine;
}

function buildDraftStorageBaseKey(therapistId: number | null | undefined, patientId: number) {
  return [
    CLINICAL_NOTE_DRAFT_STORAGE_PREFIX,
    `therapist:${therapistId ?? 'unknown'}`,
    `patient:${patientId}`,
  ].join(':');
}

function buildDraftStorageKey(baseKey: string, context: DraftContext) {
  return context.mode === 'create' ? `${baseKey}:create` : `${baseKey}:edit:${context.noteId}`;
}

function removeStoredDraft(storageKey: string) {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(storageKey);
}

function readStoredDraft(storageKey: string): StoredClinicalNoteDraft | null {
  if (typeof window === 'undefined') return null;

  const raw = window.localStorage.getItem(storageKey);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredClinicalNoteDraft;
    const savedAt = new Date(parsed.savedAt).getTime();

    if (!parsed.savedAt || Number.isNaN(savedAt) || Date.now() - savedAt > CLINICAL_NOTE_DRAFT_TTL_MS) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    if (typeof parsed.body !== 'string' || typeof parsed.baselineBody !== 'string') {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    return parsed;
  } catch {
    window.localStorage.removeItem(storageKey);
    return null;
  }
}

function writeStoredDraft(storageKey: string, draft: StoredClinicalNoteDraft) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(storageKey, JSON.stringify(draft));
}

function areDraftsEquivalent(draft: StoredClinicalNoteDraft, persistedBody: string) {
  return draft.body === persistedBody;
}

function sweepStoredClinicalNoteDrafts() {
  if (typeof window === 'undefined') return;

  Object.keys(window.localStorage)
    .filter((key) => key.startsWith(CLINICAL_NOTE_DRAFT_STORAGE_PREFIX))
    .forEach((key) => {
      readStoredDraft(key);
    });
}

export default function ClinicalNotesPage({
  patient,
  notes: initialNotes,
  selectedNoteId,
  hideChrome = false,
}: Props) {
  const page = usePage<{ currentTherapist?: { id: number } }>();
  const therapistId = page.props.currentTherapist?.id ?? null;
  const [notes, setNotes] = useState<NormalizedClinicalNote[]>(initialNotes.map(normalizeNote));
  const rememberKey = useMemo(() => `clinical-notes:${patient.id}`, [patient.id]);
  const draftStorageBaseKey = useMemo(
    () => buildDraftStorageBaseKey(therapistId, patient.id),
    [patient.id, therapistId],
  );
  const initialActiveNoteId = selectedNoteId ?? initialNotes[0]?.id ?? null;
  const initialMode: EditorMode = selectedNoteId || initialNotes.length > 0 ? 'view' : 'create';
  const [activeNoteId, setActiveNoteId] = useRemember<number | null>(
    initialActiveNoteId,
    `${rememberKey}:activeNoteId`,
  );
  const [mode, setMode] = useRemember<EditorMode>(initialMode, `${rememberKey}:mode`);
  const [draftBody, setDraftBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshingSummary, setIsRefreshingSummary] = useState(false);
  const [searchTerm, setSearchTerm] = useRemember<string>('', `${rememberKey}:searchTerm`);
  const [recoveryDialog, setRecoveryDialog] = useState<RecoveryDialogState | null>(null);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const ignoreNextPopStateRef = useRef(false);
  const initializedContextRef = useRef<string | null>(null);

  const filteredNotes = useMemo(() => {
    if (!searchTerm.trim()) return notes;
    const query = searchTerm.trim().toLowerCase();
    return notes.filter((note) => note.body.toLowerCase().includes(query));
  }, [notes, searchTerm]);

  const activeNote = useMemo(() => {
    if (activeNoteId) {
      const explicit = notes.find((note) => note.id === activeNoteId);
      if (explicit) return explicit;
    }
    return filteredNotes[0] ?? null;
  }, [notes, filteredNotes, activeNoteId]);

  const currentDraftContext = useMemo<DraftContext | null>(() => {
    if (mode === 'create') return { mode: 'create' };
    if (mode === 'edit' && activeNote) {
      return { mode: 'edit', noteId: activeNote.id };
    }
    return null;
  }, [mode, activeNote]);

  const currentDraftStorageKey = useMemo(
    () =>
      currentDraftContext ? buildDraftStorageKey(draftStorageBaseKey, currentDraftContext) : null,
    [currentDraftContext, draftStorageBaseKey],
  );

  const currentPersistedBody = mode === 'edit' && activeNote ? activeNote.body : '';

  const hasUnsavedChanges = useMemo(() => {
    if (!currentDraftContext) return false;
    return draftBody !== currentPersistedBody;
  }, [currentDraftContext, currentPersistedBody, draftBody]);

  const draftStorageDebounce = useDebounce(
    ({ storageKey, draft }: { storageKey: string; draft: StoredClinicalNoteDraft }) => {
      writeStoredDraft(storageKey, draft);
    },
    250,
  );

  const persistDraftSnapshot = useCallback(
    (
      context: DraftContext | null,
      body: string,
      baselineBody: string,
      { immediate = false }: { immediate?: boolean } = {},
    ) => {
      if (!context) return;

      const storageKey = buildDraftStorageKey(draftStorageBaseKey, context);
      if (body === baselineBody) {
        draftStorageDebounce.signalCancel();
        removeStoredDraft(storageKey);
        return;
      }

      const payload = {
        storageKey,
        draft: {
          body,
          baselineBody,
          savedAt: new Date().toISOString(),
        },
      };

      if (immediate) {
        draftStorageDebounce.forceSignalStart(payload);
        return;
      }

      draftStorageDebounce.signalStart(payload);
    },
    [draftStorageBaseKey, draftStorageDebounce],
  );

  const clearDraftContext = useCallback(
    (context: DraftContext | null) => {
      if (!context) return;
      draftStorageDebounce.signalCancel();
      removeStoredDraft(buildDraftStorageKey(draftStorageBaseKey, context));
    },
    [draftStorageBaseKey, draftStorageDebounce],
  );

  const persistCurrentDraftImmediately = useCallback(() => {
    persistDraftSnapshot(currentDraftContext, draftBody, currentPersistedBody, { immediate: true });
  }, [currentDraftContext, currentPersistedBody, draftBody, persistDraftSnapshot]);

  useEffect(() => {
    sweepStoredClinicalNoteDrafts();
  }, []);

  useEffect(() => {
    if (notes.length === 0) {
      if (mode === 'view') setActiveNoteId(null);
      return;
    }

    if (activeNoteId && notes.some((note) => note.id === activeNoteId)) {
      return;
    }

    if (mode === 'view') {
      setActiveNoteId(filteredNotes[0]?.id ?? notes[0]?.id ?? null);
    }
  }, [activeNoteId, filteredNotes, mode, notes, setActiveNoteId]);

  useEffect(() => {
    if (!currentDraftContext || !currentDraftStorageKey) {
      initializedContextRef.current = null;
      setDraftBody('');
      return;
    }

    if (initializedContextRef.current === currentDraftStorageKey) {
      return;
    }

    initializedContextRef.current = currentDraftStorageKey;
    const storedDraft = readStoredDraft(currentDraftStorageKey);

    if (!storedDraft || areDraftsEquivalent(storedDraft, currentPersistedBody)) {
      if (storedDraft) {
        removeStoredDraft(currentDraftStorageKey);
      }
      setDraftBody(currentPersistedBody);
      return;
    }

    setDraftBody(currentPersistedBody);
    setRecoveryDialog({
      context: currentDraftContext,
      draft: storedDraft,
      storageKey: currentDraftStorageKey,
      hasConflict: storedDraft.baselineBody !== currentPersistedBody,
    });
  }, [currentDraftContext, currentDraftStorageKey, currentPersistedBody]);

  const isRecoveryPendingForCurrentContext = recoveryDialog?.storageKey === currentDraftStorageKey;

  useEffect(() => {
    if (!currentDraftContext || !currentDraftStorageKey || isRecoveryPendingForCurrentContext) return;

    persistDraftSnapshot(currentDraftContext, draftBody, currentPersistedBody);
  }, [
    currentDraftContext,
    currentDraftStorageKey,
    currentPersistedBody,
    draftBody,
    isRecoveryPendingForCurrentContext,
    persistDraftSnapshot,
  ]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      persistCurrentDraftImmediately();
      event.preventDefault();
      event.returnValue = '';
    };

    const handlePageHide = () => {
      persistCurrentDraftImmediately();
    };

    const handlePopState = () => {
      if (ignoreNextPopStateRef.current) {
        ignoreNextPopStateRef.current = false;
        return;
      }

      persistCurrentDraftImmediately();
      if (
        !window.confirm(
          'Hai modifiche non salvate solo su questo dispositivo. Vuoi davvero uscire?',
        )
      ) {
        ignoreNextPopStateRef.current = true;
        window.history.go(1);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('popstate', handlePopState);

    const removeListener = router.on('before', (event) => {
      persistCurrentDraftImmediately();
      if (
        !window.confirm(
          'Hai modifiche non salvate solo su questo dispositivo. Vuoi davvero uscire?',
        )
      ) {
        event.preventDefault();
      }
    });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('popstate', handlePopState);
      removeListener();
    };
  }, [hasUnsavedChanges, persistCurrentDraftImmediately]);

  const openCreate = useCallback(() => {
    persistCurrentDraftImmediately();
    setMode('create');
    setActiveNoteId(null);
  }, [persistCurrentDraftImmediately, setActiveNoteId, setMode]);

  const openEdit = useCallback(() => {
    if (!activeNote) return;
    persistCurrentDraftImmediately();
    setMode('edit');
    setActiveNoteId(activeNote.id);
  }, [activeNote, persistCurrentDraftImmediately, setActiveNoteId, setMode]);

  const closeEditor = useCallback(() => {
    setMode('view');
    if (!activeNoteId && notes[0]) {
      setActiveNoteId(notes[0].id);
    }
  }, [activeNoteId, notes, setActiveNoteId, setMode]);

  const requestCloseEditor = useCallback(() => {
    if (!hasUnsavedChanges) {
      closeEditor();
      return;
    }
    setIsCloseDialogOpen(true);
  }, [closeEditor, hasUnsavedChanges]);

  const handleCreate = async () => {
    if (!draftBody.trim()) {
      window.alert('Inserisci del contenuto prima di salvare.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await csrfFetch(`/therapists/patients/${patient.id}/clinical_notes`, {
        method: 'POST',
        body: JSON.stringify({ clinical_note: { body: draftBody.trim() } }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.errors?.join(', ') || 'Errore nel salvataggio della nota');
      }

      const data = (await response.json()) as { note: ClinicalNote };
      clearDraftContext({ mode: 'create' });
      setNotes((prev) => [normalizeNote(data.note), ...prev]);
      setSearchTerm('');
      setActiveNoteId(data.note.id);
      setMode('view');
      setDraftBody('');
    } catch (error) {
      logError(error);
      window.alert('Impossibile salvare la nota clinica. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!activeNote) return;
    if (!draftBody.trim()) {
      window.alert('La nota non può essere vuota.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await csrfFetch(
        `/therapists/patients/${patient.id}/clinical_notes/${activeNote.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ clinical_note: { body: draftBody.trim() } }),
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.errors?.join(', ') || "Errore nell'aggiornamento della nota");
      }

      const data = (await response.json()) as { note: ClinicalNote };
      clearDraftContext({ mode: 'edit', noteId: activeNote.id });
      setNotes((prev) =>
        prev.map((note) => (note.id === data.note.id ? normalizeNote(data.note) : note)),
      );
      setMode('view');
      setDraftBody('');
    } catch (error) {
      logError(error);
      window.alert('Impossibile aggiornare la nota clinica. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!activeNote) return;
    if (!window.confirm('Eliminare definitivamente questa nota clinica?')) return;

    setIsSubmitting(true);
    try {
      const response = await csrfFetch(
        `/therapists/patients/${patient.id}/clinical_notes/${activeNote.id}`,
        {
          method: 'DELETE',
        },
      );

      if (!response.ok) {
        throw new Error('Impossibile eliminare la nota');
      }

      clearDraftContext({ mode: 'edit', noteId: activeNote.id });
      const remaining = notes.filter((note) => note.id !== activeNote.id);
      setNotes(remaining);
      setDraftBody('');

      if (remaining.length === 0) {
        setActiveNoteId(null);
        setMode('view');
      } else {
        setActiveNoteId(remaining[0]?.id ?? null);
        setMode('view');
      }
    } catch (error) {
      logError(error);
      window.alert("Errore durante l'eliminazione della nota.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshSummary = async () => {
    if (!activeNote) return;
    setIsRefreshingSummary(true);
    try {
      const response = await csrfFetch(
        `/therapists/patients/${patient.id}/clinical_notes/${activeNote.id}/refresh_summary`,
        {
          method: 'POST',
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.errors?.join(', ') || 'Errore rigenerazione sintesi');
      }

      const data = (await response.json()) as { note: ClinicalNote };
      setNotes((prev) =>
        prev.map((note) => (note.id === data.note.id ? normalizeNote(data.note) : note)),
      );
    } catch (error) {
      logError(error);
      window.alert('Impossibile rigenerare la sintesi. Riprova più tardi.');
    } finally {
      setIsRefreshingSummary(false);
    }
  };

  const content = (
    <div className="w-full space-y-8">
      {!hideChrome &&
        breadcrumbs([
          { text: 'Clienti', url: '/therapists/patients' },
          {
            text: patient.display_name,
            url: `/therapists/patients/${patient.id}`,
          },
          {
            text: 'Note',
            current: true,
          },
        ])}
      {!hideChrome && <PatientPageHeader patient={patient} active="clinical_notes" />}

      <div className="grid flex-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <ClinicalNotesSidebar
          notes={filteredNotes}
          activeNoteId={activeNoteId}
          mode={mode}
          onSelect={(id) => {
            persistCurrentDraftImmediately();
            setActiveNoteId(id);
            setMode('view');
          }}
          onCreate={openCreate}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          formatDateTime={formatDateTime}
          previewText={createPreview}
        />

        <section className="flex min-h-[540px] flex-1 flex-col gap-6">
          <main className="flex h-full flex-1 flex-col gap-6">
            {mode === 'create' ? (
              <ClinicalNoteEditor
                mode="create"
                body={draftBody}
                isSubmitting={isSubmitting}
                onCancel={requestCloseEditor}
                onChange={setDraftBody}
                onSubmit={handleCreate}
                formatDateTime={formatDateTime}
              />
            ) : null}

            {mode === 'view' && activeNote ? (
              <ClinicalNoteViewer
                note={activeNote}
                isRefreshing={isRefreshingSummary}
                isDeleting={isSubmitting}
                onRefresh={handleRefreshSummary}
                onEdit={openEdit}
                onDelete={handleDelete}
                formatDateTime={formatDateTime}
              />
            ) : null}

            {mode === 'edit' && activeNote ? (
              <ClinicalNoteEditor
                mode="edit"
                body={draftBody}
                createdAt={activeNote.created_at}
                isSubmitting={isSubmitting}
                onCancel={requestCloseEditor}
                onChange={setDraftBody}
                onSubmit={handleUpdate}
                formatDateTime={formatDateTime}
              />
            ) : null}

            {mode === 'view' && !activeNote && notes.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/60 p-10 text-center text-muted-foreground">
                <FileText className="h-10 w-10" />
                <p>Nessuna nota clinica registrata. Inizia creando la prima nota.</p>
                <Button onClick={openCreate}>
                  <Plus className="mr-2 h-4 w-4" /> Crea nota
                </Button>
              </div>
            ) : null}
          </main>
        </section>
      </div>
    </div>
  );

  const dialogs = (
    <>
      <Dialog open={!!recoveryDialog} onOpenChange={() => undefined}>
        <DialogContent
          className="sm:max-w-lg"
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {recoveryDialog?.context.mode === 'create'
                ? 'Recuperare la nuova nota non salvata?'
                : 'Recuperare le modifiche non salvate?'}
            </DialogTitle>
            <DialogDescription>
              {recoveryDialog?.context.mode === 'create'
                ? `Abbiamo trovato una nuova nota salvata solo su questo dispositivo il ${formatDateTime(recoveryDialog?.draft.savedAt)}.`
                : recoveryDialog?.hasConflict
                  ? `Abbiamo trovato una bozza locale salvata il ${formatDateTime(recoveryDialog?.draft.savedAt)}. La nota salvata è cambiata nel frattempo.`
                  : `Abbiamo trovato una bozza locale per questa nota, salvata il ${formatDateTime(recoveryDialog?.draft.savedAt)}.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (recoveryDialog) {
                  removeStoredDraft(recoveryDialog.storageKey);
                }
                setRecoveryDialog(null);
              }}
            >
              Apri versione salvata
            </Button>
            <Button
              onClick={() => {
                if (recoveryDialog) {
                  setDraftBody(recoveryDialog.draft.body);
                }
                setRecoveryDialog(null);
              }}
            >
              Recupera bozza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCloseDialogOpen} onOpenChange={setIsCloseDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Chiudere l&apos;editor?</DialogTitle>
            <DialogDescription>
              Se esci ora, la bozza resta salvata solo su questo dispositivo finché non la scarti o
              la salvi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsCloseDialogOpen(false)}>
              Continua a scrivere
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                persistCurrentDraftImmediately();
                setIsCloseDialogOpen(false);
                closeEditor();
              }}
            >
              Chiudi e conserva bozza
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                clearDraftContext(currentDraftContext);
                setDraftBody(currentPersistedBody);
                setIsCloseDialogOpen(false);
                closeEditor();
              }}
            >
              Scarta bozza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (hideChrome) {
    return (
      <>
        {content}
        {dialogs}
      </>
    );
  }

  return (
    <Layout isAdmin user={{ name: 'Therapist' }}>
      {content}
      {dialogs}
    </Layout>
  );
}
